import { Link } from 'react-router-dom';
import { Search, Map, ArrowRight, Home, Building, TreePine, Landmark, MousePointerClick, CalendarCheck, KeyRound, Trophy, ClipboardList } from 'lucide-react';
import { NewsBeepsCarousel } from '@/components/news/NewsBeepsCarousel';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SearchBar } from '@/components/search/SearchBar';
import { PropertyGrid } from '@/components/property/PropertyGrid';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeaturedProperties } from '@/hooks/useProperties';
import { useNeighborhoods } from '@/hooks/useNeighborhoods';
import { useGlobalTopRanked } from '@/hooks/useRankedListings';
import { RankedPropertyCard } from '@/components/rankings/RankedPropertyCard';
import { useCity } from '@/contexts/CityContext';
import { useMemo } from 'react';
import { LocalBusinessSchema, OrganizationSchema, FAQSchema } from '@/components/seo/StructuredData';
import { Helmet } from 'react-helmet-async';
import { ViewingPlanCTA } from '@/components/ViewingPlanCTA';

// Prefetch the Map page module on hover for faster navigation
const prefetchMap = () => {
  import('@/pages/Map');
};
const PROPERTY_TYPES = [
  { icon: Home, label: 'Houses', type: 'House' },
  { icon: Building, label: 'Condos', type: 'Condo' },
  { icon: TreePine, label: 'Townhouses', type: 'Townhouse' },
  { icon: Building, label: 'Multi-Family', type: 'Multi-Family' },
  { icon: Landmark, label: 'Land', type: 'Land' },
];

// Nearby cities that should be shown separately (not as neighborhoods)
const NEARBY_CITIES = new Set([
  'St. Albert', 'Sherwood Park', 'Spruce Grove', 'Leduc', 'Beaumont',
  'Airdrie', 'Cochrane', 'Chestermere', 'Okotoks', 'Strathmore'
]);

const PRICE_RANGES = [
  { label: 'Under $300K', min: 0, max: 300000 },
  { label: '$300K - $400K', min: 300000, max: 400000 },
  { label: '$400K - $500K', min: 400000, max: 500000 },
  { label: '$500K - $600K', min: 500000, max: 600000 },
  { label: '$600K - $700K', min: 600000, max: 700000 },
  { label: '$700K - $800K', min: 700000, max: 800000 },
  { label: '$800K - $1M', min: 800000, max: 1000000 },
  { label: '$1M - $1.5M', min: 1000000, max: 1500000 },
  { label: 'Over $1.5M', min: 1500000, max: undefined },
];

const FAQS = [
  {
    question: "Do I need to call an agent to book a viewing?",
    answer: "No! Our entire process is online. Simply find a property you like, click 'Book a Viewing', and submit your preferred time. You'll receive a confirmation email with all the details — no phone calls required."
  },
  {
    question: "How quickly will I get a response after booking?",
    answer: "Most viewing requests are confirmed within 24 hours. You'll receive an email with the confirmed date, time, and any instructions for your visit."
  },
  {
    question: "Is there a fee to book a viewing?",
    answer: "No, booking viewings through our platform is completely free. We connect you directly with listings at no cost."
  },
  {
    question: "Can I book multiple viewings at once?",
    answer: "Yes! You can book as many viewings as you'd like. We recommend scheduling them with enough time between each so you can properly explore each property."
  },
  {
    question: "What if I need to cancel or reschedule?",
    answer: "No problem. You'll receive a confirmation email with options to reschedule or cancel. We ask for at least 24 hours notice when possible."
  },
  {
    question: "Do I need an account to book a viewing?",
    answer: "You can book a viewing without an account, but creating a free account lets you save favorites, track your viewing history, and set up alerts for new listings."
  }
];

export default function Index() {
  const { city } = useCity();
  const { data: featuredProperties, isLoading } = useFeaturedProperties(city, 6);
  const { data: neighborhoodsData, isLoading: isLoadingNeighborhoods } = useNeighborhoods();
  const {
    data: topRankedListings,
    isLoading: isLoadingTopRanked,
    isError: isTopRankedError,
    error: topRankedError,
    refetch: refetchTopRanked,
  } = useGlobalTopRanked(6, city);

  // Get neighborhoods and nearby cities for the current city
  const { neighborhoods, nearbyCities } = useMemo(() => {
    if (!neighborhoodsData?.grouped) {
      return { neighborhoods: [], nearbyCities: [] };
    }

    const cityNeighborhoods = neighborhoodsData.grouped[city] || [];
    const topNeighborhoods = cityNeighborhoods
      .filter(n => !NEARBY_CITIES.has(n.neighborhood))
      .slice(0, 16)
      .map(n => n.neighborhood);

    const nearbyCitiesList = neighborhoodsData.sortedCities
      .filter(c => NEARBY_CITIES.has(c))
      .slice(0, 5);

    return { neighborhoods: topNeighborhoods, nearbyCities: nearbyCitiesList };
  }, [neighborhoodsData, city]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Book Viewing | Book Home Viewings Online in Edmonton & Calgary</title>
        <meta name="description" content="Search properties and book home viewings online in Edmonton and Calgary. No phone calls needed. Find houses, condos, and townhouses for sale." />
        <link rel="canonical" href="https://bookviewing.ca/" />
        <meta property="og:title" content="Book Viewing | Book Home Viewings Online" />
        <meta property="og:description" content="Search properties and book home viewings online in Edmonton and Calgary. No phone calls needed." />
        <meta property="og:url" content="https://bookviewing.ca/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Structured Data */}
      <OrganizationSchema />
      <LocalBusinessSchema />
      <FAQSchema faqs={FAQS} />

      <Header />

      <main>
        {/* SECTION 1: Hero — Search Bar front and center, minimal padding on mobile */}
        <section className="relative bg-primary py-10 md:py-32 hero-lcp" style={{ contain: 'layout style paint' }}>
          <div className="container relative z-10">
            <div className="max-w-3xl mx-auto text-center text-primary-foreground mb-6 md:mb-10">
              <h1 
                className="font-display text-3xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-4 text-balance"
                style={{ contentVisibility: 'auto' }}
              >
                Find Your Next Home
              </h1>
              <p className="text-sm md:text-xl text-primary-foreground/95">
                Search & book viewings online — no phone calls needed.
              </p>
            </div>
            
            <SearchBar variant="hero" />

            {/* Quick action buttons — 2x2 grid, large tap targets */}
            <div className="grid grid-cols-2 gap-3 mt-6 md:mt-8 max-w-lg mx-auto">
              <Link to="/search?advanced=true" className="block">
                <Button variant="secondary" className="w-full gap-2 h-12 text-sm bg-background text-foreground hover:bg-background/90">
                  <Search className="w-4 h-4" />
                  Advanced Search
                </Button>
              </Link>
              <Link to="/map" onMouseEnter={prefetchMap} onFocus={prefetchMap} className="block">
                <Button variant="secondary" className="w-full gap-2 h-12 text-sm bg-background text-foreground hover:bg-background/90">
                  <Map className="w-4 h-4" />
                  Map View
                </Button>
              </Link>
              <a href="#top-ranked" className="block">
                <Button variant="secondary" className="w-full gap-2 h-12 text-sm bg-background text-foreground hover:bg-background/90">
                  <Trophy className="w-4 h-4" />
                  See Top Ranked
                </Button>
              </a>
              <Link to="/viewing-plan" className="block">
                <Button variant="secondary" className="w-full gap-2 h-12 text-sm bg-background text-foreground hover:bg-background/90">
                  <ClipboardList className="w-4 h-4" />
                  Viewing Plan
                </Button>
              </Link>
            </div>
          </div>

          {/* Decorative elements — hidden on mobile for speed */}
          <div className="absolute inset-0 overflow-hidden hidden md:block">
            <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-primary-foreground/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 bg-primary-foreground/5 rounded-full blur-3xl" />
          </div>
        </section>

        {/* SECTION 2: Browse by Price — low-friction entry point */}
        <section className="py-10 md:py-16 bg-background">
          <div className="container">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">
                Browse by Price in {city}
              </h2>
              <p className="text-muted-foreground text-sm md:text-base">Find homes that fit your budget</p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 md:gap-3 max-w-4xl mx-auto">
              {PRICE_RANGES.map((range) => (
                <Link
                  key={range.label}
                  to={`/search?minPrice=${range.min}${range.max ? `&maxPrice=${range.max}` : ''}`}
                  className="group"
                >
                  <div className="bg-card border rounded-lg p-3 md:p-4 text-center hover:border-primary hover:shadow-md transition-all">
                    <p className="font-semibold text-xs md:text-sm group-hover:text-primary transition-colors">
                      {range.label}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3: Featured Listings — visual proof / eye candy */}
        <section className="py-10 md:py-16">
          <div className="container">
            <div className="flex items-center justify-between mb-6 md:mb-10">
              <div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">Featured Listings</h2>
                <p className="text-muted-foreground text-sm md:text-base">Handpicked properties just for you</p>
              </div>
              <Link to="/search">
                <Button variant="outline" className="gap-2">
                  View All <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <PropertyGrid properties={featuredProperties} isLoading={isLoading} />
          </div>
        </section>

        {/* SECTION 4: Top Ranked */}
        <section id="top-ranked" className="py-10 md:py-16 bg-secondary/50">
          <div className="container">
            <div className="flex items-center justify-between mb-6 md:mb-10">
              <div>
                <div className="flex items-center gap-2 mb-1 md:mb-2">
                  <Trophy className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  <h2 className="font-display text-2xl md:text-3xl font-semibold">Top Ranked</h2>
                </div>
                <p className="text-muted-foreground text-sm md:text-base">
                  Best value listings scored by price, photos & freshness
                </p>
              </div>
              <Link to="/top-ranked">
                <Button variant="outline" className="gap-2">
                  View All <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {isLoadingTopRanked ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-xl" />
                ))}
              </div>
            ) : isTopRankedError ? (
              <div className="text-center py-12 bg-card rounded-xl">
                <Trophy className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Couldn't load rankings.</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {topRankedError instanceof Error ? topRankedError.message : 'Please try again.'}
                </p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => refetchTopRanked()}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : topRankedListings && topRankedListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
                {topRankedListings.map((rankedListing) => (
                  <RankedPropertyCard
                    key={rankedListing.id}
                    rankedListing={rankedListing}
                    showBreakdown={false}
                    communityName={rankedListing.community?.name}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card rounded-xl">
                <Trophy className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No ranked listings found yet.</p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => refetchTopRanked()}>
                    Refresh
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SECTION 5: Browse by Property Type */}
        <section className="py-10 md:py-16 bg-secondary/50">
          <div className="container">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">Browse by Property Type</h2>
              <p className="text-muted-foreground text-sm md:text-base">Find the perfect space for your lifestyle</p>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {PROPERTY_TYPES.map(({ icon: Icon, label, type }) => (
                <Link
                  key={type}
                  to={`/search?type=${type}`}
                  className="group bg-card rounded-xl p-4 md:p-6 text-center shadow-sm hover:shadow-md transition-all hover:-translate-y-1"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 md:mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon className="w-6 h-6 md:w-7 md:h-7" />
                  </div>
                  <h3 className="font-semibold text-sm md:text-base">{label}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6: How It Works — trust builder, lower priority */}
        <section className="py-10 md:py-16 bg-background">
          <div className="container">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">How It Works</h2>
              <p className="text-muted-foreground text-sm md:text-base">Book viewings in 3 simple steps — no phone calls required</p>
            </div>

            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  <MousePointerClick className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <p className="text-xs font-medium text-primary mb-1 md:mb-2">Step 1</p>
                <h3 className="font-semibold text-sm md:text-lg mb-1 md:mb-2">Find</h3>
                <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                  Search listings by location, price, or features.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  <CalendarCheck className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <p className="text-xs font-medium text-primary mb-1 md:mb-2">Step 2</p>
                <h3 className="font-semibold text-sm md:text-lg mb-1 md:mb-2">Book</h3>
                <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                  Click "Book a Viewing" and pick your time — no calls.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  <KeyRound className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <p className="text-xs font-medium text-primary mb-1 md:mb-2">Step 3</p>
                <h3 className="font-semibold text-sm md:text-lg mb-1 md:mb-2">Visit</h3>
                <p className="text-muted-foreground text-xs md:text-sm hidden md:block">
                  Get confirmation and tour your potential new home.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* News Beeps Carousel */}
        <NewsBeepsCarousel />

        {/* Popular Neighborhoods */}
        <section className="py-10 md:py-16 bg-secondary/50">
          <div className="container">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">
                Popular Neighborhoods in {city}
              </h2>
              <p className="text-muted-foreground text-sm md:text-base">Explore top areas near you</p>
            </div>

            {/* City Hub Links */}
            <div className="max-w-4xl mx-auto mb-8">
              <h3 className="text-center text-sm font-medium text-muted-foreground mb-4">
                Explore by City
              </h3>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Link
                  to="/edmonton"
                  className="group bg-card rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 border border-transparent hover:border-primary/20"
                >
                  <h4 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                    Edmonton Homes for Sale
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Browse all Edmonton listings, area guides & market info
                  </p>
                </Link>
                <Link
                  to="/calgary"
                  className="group bg-card rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 border border-transparent hover:border-primary/20"
                >
                  <h4 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                    Calgary Homes for Sale
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Browse all Calgary listings, community guides & more
                  </p>
                </Link>
              </div>
              {city === 'Edmonton' && (
                <>
                  <h3 className="text-center text-sm font-medium text-muted-foreground mb-4">
                    Explore by Area
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link
                      to="/edmonton/south-edmonton-homes-for-sale"
                      className="group bg-card rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 border border-transparent hover:border-primary/20"
                    >
                      <h4 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                        South Edmonton
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Tamarack, Laurel, The Orchards & more
                      </p>
                    </Link>
                    <Link
                      to="/edmonton/southwest-edmonton-homes-for-sale"
                      className="group bg-card rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 border border-transparent hover:border-primary/20"
                    >
                      <h4 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                        Southwest Edmonton
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Windermere, Keswick, Ambleside & more
                      </p>
                    </Link>
                    <Link
                      to="/edmonton/southeast-edmonton-homes-for-sale"
                      className="group bg-card rounded-xl p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 border border-transparent hover:border-primary/20"
                    >
                      <h4 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                        Southeast Edmonton
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Meadows, Wild Rose, Millwoods & more
                      </p>
                    </Link>
                  </div>
                </>
              )}
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {/* Neighborhoods */}
              <div className="bg-card rounded-xl p-6 shadow-sm">
                {isLoadingNeighborhoods ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <Skeleton key={i} className="h-8 w-24 rounded-full" />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center gap-2">
                    {neighborhoods.map((neighborhood) => (
                      <Link
                        key={`${city}-${neighborhood}`}
                        to={`/search?neighborhood=${encodeURIComponent(neighborhood)}`}
                      >
                        <Button variant="outline" size="sm" className="rounded-full">
                          {neighborhood}
                        </Button>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Nearby Cities */}
              {nearbyCities.length > 0 && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-3">Nearby Cities</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {nearbyCities.map((nearbyCity) => (
                      <Link
                        key={nearbyCity}
                        to={`/search?q=${encodeURIComponent(nearbyCity)}`}
                      >
                        <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-foreground">
                          {nearbyCity}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-16">
          <div className="container">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-1 md:mb-2">Frequently Asked Questions</h2>
              <p className="text-muted-foreground text-sm md:text-base">Everything you need to know about booking viewings online</p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left font-medium text-sm md:text-base">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-primary text-primary-foreground">
          <div className="container text-center">
            <h2 className="font-display text-2xl md:text-4xl font-semibold mb-3 md:mb-4">
              Ready to Book Your Viewing?
            </h2>
            <p className="text-primary-foreground/80 mb-6 md:mb-8 max-w-xl mx-auto text-sm md:text-base">
              Create a free account to save properties, set up alerts, and book viewings online.
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              <Link to="/auth?mode=signup">
                <Button size="lg" className="gap-2 bg-background text-foreground hover:bg-background/90">
                  Get Started Free <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/search">
                <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  Browse Listings
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
